import React, { lazy, Suspense } from "react";
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, useParams } from "react-router-dom";
import { ReactFlowProvider } from "reactflow";

// Import AppLayout directly (not lazy) to prevent sidebar/header reloading
import LoginAppsPage from "../login/LoginAppsPage";
import CallFlow from "../voice/components/CallFlow";
import AppLayout from "./AppLayout";
import { CommonLoadingV2 } from "./utils/CommonVessels";

// Lazy load route components for code splitting
const NoAccessToExternalPage = lazy(() => import("../voice/components/info-component/NoAccessToExternalPage"));
const CallHandlingFlowChart = lazy(() => import("../voice/components/CallHandlingFlowChart"));
const Dialpad = lazy(() => import("../voice/components/Dialpad"));
const VoiceAnalytics = lazy(() => import("../voice/components/VoiceAnalytics"));
const VoiceCallLog = lazy(() => import("../voice/components/VoiceCallLog"));
const ShowAutomationComponentForm = lazy(() => import("./../pages/automation/ShowAutomationComponentForm"));
const ZohoCRMLandingPage = lazy(() => import("./../pages/services/ZohoCRMLandingPage"));
const TeamsLandingPage = lazy(() => import("./../pages/services/TeamsLandingPage"));
const AutomatedMessages = lazy(() => import("./AutomatedMessages"));
const BlockedNumbers = lazy(() => import("./BlockedNumbers"));
const CampaignAnalytics = lazy(() => import("./CampaignAnalytics"));
const ContentGpt = lazy(() => import("./Content"));
const ConversationWrapper = lazy(() => import("./ConversationContext"));
const ConversationView = lazy(() => import("./ConversationPage"));
const Home = lazy(() => import("./Home"));
const MessageLogs = lazy(() => import("./MessageLogs"));
const Module = lazy(() => import("./Module"));
const TenDlc = lazy(() => import("./OnboardingComponents/10DLC/TenDlc"));
const ActiveNumbers = lazy(() => import("./OnboardingComponents/ActiveNumbers"));
const ChannelsConfigPage = lazy(() => import("./OnboardingComponents/ChannelsConfiguration"));
const GeneralSettings = lazy(() => import("./Setting/GeneralAppsSettings"));
const Settings = lazy(() => import("./Setting/Settings"));
const Templates = lazy(() => import("./Templates"));
const Users = lazy(() => import("./Users"));
const AutomationMessageLogs = lazy(() => import("./automation/AutomationMessageLogs"));
const Campaign = lazy(() => import("./campaign/components/campaign/Campaign"));
const CampaignOverview = lazy(() => import("./campaign/components/overview/CampaignOverview"));
const SendSMS = lazy(() => import("./outbound/SendSMS"));
const TextSMS = lazy(() => import("./services/TextSMS"));
const TextSMSAuthPage = lazy(() => import("./services/TextSMSAuthPage"));
const Teams = lazy(() => import("./Teams"));
const NoAccess = lazy(() => import("../pages/noAccess"));
const PermissionRoute = lazy(() => import("../components/PermissionRoute"));
const { BackToChannelsButton } = require("./services/TextSMSAuthPage");
const EmailLoginComponent = lazy(() => import("./Login/EmailLoginComponent"));
const LogoutPage = lazy(() => import("./LogoutPage"));
const AccountDeletedPage = lazy(() => import("./AccountDeletedPage"));
const Contacts = lazy(() => import("./contacts/Contacts"));
const DripCampaign = lazy(() => import("./DripCampaign"));
const AIDataSources = lazy(() => import("./ai/DataSources"));
const AIFeaturesListingPage = lazy(() => import("./ai/AIFeaturesListingPage"));
const Playground = lazy(() => import("./ai/Playground"));
const GetStarted = lazy(() => import("./GetStarted"));
const CreditDashboard = lazy(() => import("../components/main/Credits/CreditDashboard"));

const ChannelRedirect = () => {
  const { serviceName } = useParams();
  if (serviceName === "helloSend") {
    return (
      <PermissionRoute module="channels">
        <ActiveNumbers backToChannelsButton={<BackToChannelsButton />} />
      </PermissionRoute>
    );
  }
  return <Navigate to={`/settings/channels/${serviceName}${window.location.search}`} replace />;
};

const createRouter = (loadingContextValue, locationState, accessDenied, setAccessDenied) => {
  const { editNodes, editEdges } = locationState || { editNodes: [], editEdges: [] };
  return createBrowserRouter(
    createRoutesFromElements(
      <>
      <Route path="no-permission" element={<Suspense fallback={<CommonLoadingV2 />}><NoAccessToExternalPage /></Suspense>} />
      <Route
        path="/"
        element={<PermissionRoute module={"external page"}><AppLayout setLoading={loadingContextValue?.setLoading} accessDenied={accessDenied} setAccessDenied={setAccessDenied} /></PermissionRoute>}
      >
        <Route
          path="/channels"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <PermissionRoute module="channels">
                <ChannelsConfigPage />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          exact
          path="/home"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <Home />
            </Suspense>
          }
        />
        <Route
          path="/voice/analytics"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <VoiceAnalytics />
            </Suspense>
          }
        />
        <Route
          path="/voice/callflow/createflow"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <ReactFlowProvider>
                <CallHandlingFlowChart />
              </ReactFlowProvider>
            </Suspense>
          }
        />
        <Route
          path="/settings/callFlow/edit/:callFlowId/phoneNumber/:phoneNumber"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <ReactFlowProvider>
                <CallHandlingFlowChart editNodes={editNodes} editEdges={editEdges} />
              </ReactFlowProvider>
            </Suspense>
          }
        />
        <Route
          path="/voice/callFlow/edit/:callFlowId/phoneNumber/:phoneNumber"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <ReactFlowProvider>
                <CallHandlingFlowChart editNodes={editNodes} editEdges={editEdges} />
              </ReactFlowProvider>
            </Suspense>
          }
        />
        <Route
          path="/voice/dialpad"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <Dialpad showDialPad={true} />
            </Suspense>
          }
        />
        <Route
          path="/voice/history"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <VoiceCallLog />
            </Suspense>
          }
        />

        <Route
          path="/voice/callFlow/list"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <CallFlow />
            </Suspense>
          }
        />
        <Route
          path="/settings/number/twilio_voice"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <PermissionRoute module="channels">
                <TextSMSAuthPage />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/settings/twilio"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <PermissionRoute module="channels">
                <TextSMSAuthPage />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          exact
          path="/settings"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <Settings />
            </Suspense>
          }
        />
        <Route
          exact
          path="/settings/general"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <GeneralSettings />
            </Suspense>
          }
        />
        <Route
          exact
          path="/settings/ai/features"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <PermissionRoute module="AI Features">
                <AIFeaturesListingPage />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/settings/ai/credits"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <PermissionRoute module="AI Features">
                <CreditDashboard />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/settings/channels/:serviceName"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <PermissionRoute module="channels">
                <TextSMSAuthPage />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/settings/teams"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <Teams />
            </Suspense>
          }
        />
        <Route
          path="/settings/number/twilio_voice"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <TextSMSAuthPage />
            </Suspense>
          }
        />
        <Route
          path="/campaign"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <Campaign />
            </Suspense>
          }
        />
        <Route
          path="/messaging/campaigns/list"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <CampaignOverview />
            </Suspense>
          }
        />
        {/* <Route path="/channels" element={<ChannelsConfigPage />} /> */}
        <Route
          path="/settings/channels"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <PermissionRoute module="channels">
                <ChannelsConfigPage />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/channels/textsms"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <PermissionRoute module="channels">
                <TextSMS />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/settings/channels/textsms"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <PermissionRoute module="channels">
                <TextSMS />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/channels/textsms/:serviceName"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <ChannelRedirect />
            </Suspense>
          }
        />
        <Route
          path="/settings/channels/textsms/:serviceName"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <ChannelRedirect />
            </Suspense>
          }
        />
        {/* <Route path="/content" element={<Suspense fallback={<CommonLoadingV2/>}><ContentGpt /></Suspense>} />
        <Route path="/chat" element={<Suspense fallback={<CommonLoadingV2/>}><ConversationView /></Suspense>} /> */}
        <Route
          path="/inbox"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <ConversationWrapper />
            </Suspense>
          }
        />
        <Route
          path="/inbox/:contactId"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <ConversationWrapper />
            </Suspense>
          }
        />
        <Route path="*" element={<p>There's nothing here: 404!</p>} />
        <Route
          path="/sendsms"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <SendSMS bulk={false} appLoading={loadingContextValue.loading} setLoading={loadingContextValue?.setLoading} />
            </Suspense>
          }
        />
        <Route
          path="/messages"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <SendSMS bulk={true} setLoading={loadingContextValue?.setLoading} />
            </Suspense>
          }
        />
        <Route
          path="/settings/templates"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <Templates />
            </Suspense>
          }
        />
        <Route
          path="/templates"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <Templates />
            </Suspense>
          }
        />
        <Route
          path="/settings/users"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <PermissionRoute module="users">
                <Users />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/users"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <Users />
            </Suspense>
          }
        />
        <Route
          path="/settings/blockedNumbers"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <BlockedNumbers />
            </Suspense>
          }
        />
        {/* <Route path="/automation" element={<WorkFlow />} /> */}
        <Route
          path="/messaging/automation"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <AutomatedMessages />
            </Suspense>
          }
        />
        <Route
          path="/messaging/automation/:eventId/logs"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <AutomationMessageLogs />
            </Suspense>
          }
        />
        <Route
          path="/messaging/automation/:eventId"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <DripCampaign />
            </Suspense>
          }
        />
        <Route
          path="/messaging/campaigns/list/:campaignId/reports"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <CampaignAnalytics currentItem={"error"} />
            </Suspense>
          }
        />
        <Route
          path="/messaging/campaigns/list/:campaignId/overview"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <CampaignAnalytics currentItem={"overview"} />
            </Suspense>
          }
        />
        <Route path="/messaging/campaigns" element={<Navigate to={`/messaging/campaigns/list${window.location.search}`} />} />
        <Route
          path="/channels/textsms/:serviceName/10DLC"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <TenDlc />
            </Suspense>
          }
        />
        <Route
          path="/messaging/logs"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <MessageLogs />
            </Suspense>
          }
        />
        <Route path="/messaging/message-logs" element={<Navigate to={`/messaging/logs${window.location.search}`} />} />
        <Route
          path="/teams/landing"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <TeamsLandingPage />
            </Suspense>
          }
        />
        <Route
          path="/:leftService/landing"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <ZohoCRMLandingPage />
            </Suspense>
          }
        />
        <Route
          path="/login"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <EmailLoginComponent />
            </Suspense>
          }
        />
        <Route
          path="/web/signin"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <EmailLoginComponent websiteLogin={true} context={"web_signin"} />
            </Suspense>
          }
        />
        <Route
          path="/web/signup"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <EmailLoginComponent websiteLogin={true} context={"web_signup"} />
            </Suspense>
          }
        />
        <Route
          path="/web/apps"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <LoginAppsPage />
            </Suspense>
          }
        />
        <Route
          path="/logout"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <LogoutPage setLoading={loadingContextValue?.setLoading} />
            </Suspense>
          }
        />
        <Route
          path="/account-deleted"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <AccountDeletedPage setLoading={loadingContextValue?.setLoading} />
            </Suspense>
          }
        />
        <Route
          path="/zoho-crm/webhook"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <ShowAutomationComponentForm source={"custom-action"} />
            </Suspense>
          }
        />
        {/* <Route path="*" element={<h1>404!</h1>} /> */}
        <Route
          path="no-access"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <NoAccess />
            </Suspense>
          }
        />
        <Route
          path="/channels/:serviceName"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <PermissionRoute module="channels">
                <ChannelRedirect />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/contacts/:recordId"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <Contacts />
            </Suspense>
          }
        />
        <Route
          path="/contacts"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <Contacts />
            </Suspense>
          }
        />
        <Route
          path="/get-started"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <GetStarted />
            </Suspense>
          }
        />
        <Route
          path="/ai/data-sources"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <PermissionRoute module="ai">
              <AIDataSources />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/ai/playground"
          element={
            <Suspense fallback={<CommonLoadingV2 />}>
              <PermissionRoute module="ai">
                <Playground />
              </PermissionRoute>
            </Suspense>
          }
        />
      </Route>
      </>)
  );
};
export default createRouter;
